import { makeAutoObservable, runInAction } from "mobx";

import { Dictionary, isUndefined } from "@noli/core";

import loopRequestResult from "@/utils/loopRequestResult";
import events from "@/utils/events";
import { isZh } from "@/utils/lang";
import {
  bindPhoneWithWeChat,
  getMemberStatus,
  getUserBaseInfo,
  getWeChatQrCodeScanStatus,
  loginByPassword,
  LoginByPasswordRequestData,
  LoginByVCodeRequestData,
  loginByPhoneVerificationCode,
  loginByWeChatQrCode,
  LoginEvent,
  logout,
  loginByEmailVerificationCode,
} from "./api";
import { MsgRevEventProps } from "@/utils/buried";
import { parametersStore } from "@/stores/parameters";
import { checkAlbum } from "../album/api";
import { isAuthUser } from "@/utils/status";
import { cancelAllTask } from "@/utils/batchTask";

// 打开登录框的标记
export enum CallTarget {
  MEMBER = "member",
  TEMPLATE = "template",
  DESIGN_PLANET = "designPlanet",
}

export interface UserInfo {
  avatar: string;
  createdAt: string;
  email: string;
  isBindFeishu: boolean;
  isBindPhone: boolean;
  isBindPwd: boolean;
  isBindWechat: boolean;
  nickname: string;
  phone: string;
  remark: string;
  salt: string;
  sex: string;
  status: string;
  updatedAt: string;
  userId: number;
  username: string;
  role_ids: null | number[];
  role_keys: null | string[];
  slogan?: string;
  banner?: string;
}

export interface UserState {
  tokenExpire: string | null;
  userInfo: Partial<UserInfo>;
  limits: {
    creatorLimit?: boolean;
  };
}

export interface LoginState {
  visible: boolean;
  closable?: boolean;
  maskClosable?: boolean;
  bindPhoneVisible: boolean;
  forgetPasswordVisible: boolean;
  agreeProtocol: boolean;
  phone: string;
  email: string;
  weChatKey: string;
  callTarget?: CallTarget;
}

export interface MemberStatusType {
  status: number;
  level: string;
  level_name: string;
  current_start_time: number;
  current_end_time: number;
  total_start_time: number;
  total_end_time: number;
}

function getDefaultState(): LoginState {
  return {
    visible: false,
    closable: true,
    maskClosable: true,
    bindPhoneVisible: false,
    forgetPasswordVisible: false,
    agreeProtocol: false,
    phone: "",
    email: "",
    weChatKey: "",
  };
}

function getDefaultUserState(): UserState {
  return {
    tokenExpire: null,
    userInfo: {},
    limits: {},
  };
}

export type DialogType = "weChat" | "phoneBind" | "phoneCode" | "account" | "forgot" | "register";
class LoginStore {
  userState: UserState = getDefaultUserState();
  memberStatus: MemberStatusType | null = null;
  loginState: LoginState = getDefaultState();
  loginAfterAction: (() => void) | undefined = undefined;
  dialogType: DialogType = isZh() ? "weChat" : "account";
  globalLoading = true;

  isGuest = false;
  headerSearchNode: JSX.Element | null = null;
  setGuest(isGuest: boolean): void {
    this.isGuest = isGuest;
  }

  get userInfo() {
    return this.userState.userInfo;
  }

  get authorization(): string | undefined {
    return this.isGuest
      ? undefined
      : localStorage.token
      ? "Bearer " + localStorage.token
      : undefined;
  }
  private _injectAuthorization(headers: Dictionary<string>, force = false): void {
    if (parametersStore.useLocalServer && !force) {
      headers["ngrok-skip-browser-warning"] = "";
      return;
    }
    const authorization = this.authorization;
    if (!isUndefined(authorization)) {
      headers.authorization = authorization;
    }
  }
  get getReqHeaders(): Dictionary<string> {
    const headers: Dictionary<string> = {};
    this._injectAuthorization(headers);
    return headers;
  }
  get getForceAuthReqHeaders(): Dictionary<string> {
    const headers: Dictionary<string> = {};
    this._injectAuthorization(headers, true);
    return headers;
  }
  get jsonPostReqHeaders(): Dictionary<string> {
    const headers: Dictionary<string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    this._injectAuthorization(headers);
    return headers;
  }
  get jsonPostForceAuthReqHeaders(): Dictionary<string> {
    const headers: Dictionary<string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    this._injectAuthorization(headers, true);
    return headers;
  }

  get showBack(): boolean {
    return this.loginState.bindPhoneVisible || this.loginState.forgetPasswordVisible;
  }

  setGlobalLoading(loading: boolean) {
    this.globalLoading = loading;
  }

  resetDialogType() {
    this.dialogType = isZh() ? "weChat" : "account";
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    window.addEventListener(
      "storage",
      (e) => {
        if (e.key == "token") {
          if (!e.newValue && e.oldValue) {
            // 退出
            this.logout();
            location.replace("/");
          } else if (e.newValue && !e.oldValue) {
            // 登录
            window.location.reload();
          }
        }
      },
      false,
    );
  }

  get hasBindPhone() {
    return this.hasUserInfo && this.userState.userInfo.phone;
  }

  get isLogin() {
    return !!localStorage.token;
  }

  get hasUserInfo() {
    return !!(this.userState.userInfo.username || this.userState.userInfo.nickname);
  }

  get hasAIGCPermission() {
    return this.hasUserInfo;
  }

  // 关闭登录对话框
  closeLoginDialog() {
    this.loginState.visible = false;
  }

  // 开启登录对话框
  openLoginDialog(payload: Partial<LoginState>, loginAfterAction?: () => void) {
    this.loginState = {
      ...getDefaultState(),
      ...payload,
      visible: true,
    };

    this.loginAfterAction = loginAfterAction;
  }

  // 开启绑定手机对话框
  openBindPhoneDialog() {
    this.loginState.bindPhoneVisible = true;
  }

  setDialogType(type: DialogType) {
    this.dialogType = type;
  }

  closeDialog() {
    this.loginState.visible = false;
    this.loginState.bindPhoneVisible = false;
    this.loginState.forgetPasswordVisible = false;
  }

  // 开启忘记密码对话框
  openForgetPasswordVisible() {
    this.loginState.forgetPasswordVisible = true;
  }

  // 关闭绑定手机和忘记密码对话框
  goBack() {
    this.loginState.bindPhoneVisible = this.loginState.forgetPasswordVisible = false;
  }

  // 设置同意协议
  setProtocol(payload: boolean) {
    this.loginState.agreeProtocol = payload;
  }

  // 设置手机号
  setPhone(payload: string) {
    this.loginState.phone = payload;
  }

  setEmail(email: string) {
    this.loginState.email = email;
  }

  // 设置微信key
  setWeChatKey(payload: string) {
    this.loginState.weChatKey = payload;
  }

  setTokenExpire(payload: string) {
    this.userState.tokenExpire = payload;
  }

  // 获取用户基本数据
  async getUserInfo() {
    const res = await getUserBaseInfo();
    if (res?.data?.user?.phone) {
      localStorage.lang = "zh";
    } else {
      localStorage.lang = "en";
    }
    if (res.errCode == 0) {
      runInAction(() => {
        this.userState.userInfo = res.data.user;
      });
    }
    return res;
  }
  // 会员状态-GetinitAuth
  async getMemberStatus() {
    const { data } = await getMemberStatus();
    runInAction(() => {
      this.memberStatus = data;
    });
  }
  // 登录后的操作
  async afterLogin(payload: { token: string; expire: string }) {
    localStorage.removeItem("sharerUid");
    localStorage.setItem("token", payload.token);
    this.setTokenExpire(payload.expire);
    await Promise.all([this.getUserInfo(), this.getMemberStatus()]);
    // await postMsgRev({
    //   eventParameters: {
    //     event_id: "log_in",
    //     event_name: MsgRevEventProps.log_in,
    //     event_type: "api",
    //   },
    // });
    checkAlbum();
    // events.emit(LoginEvent.LOGIN, {
    //   type: LoginEvent.LOGIN,
    // });

    this.loginAfterAction?.();
  }

  // 验证码登录
  async loginByVerificationCode(payload: LoginByVCodeRequestData) {
    const request = payload.email ? loginByEmailVerificationCode : loginByPhoneVerificationCode;
    const res = await request(payload);
    if (res.errCode == 0) this.afterLogin({ token: res.token, expire: res.expire });
    return res;
  }

  // 密码登录
  async loginByPassword(payload: LoginByPasswordRequestData) {
    const res = await loginByPassword(payload);
    if (res.errCode == 0) this.afterLogin({ token: res.token, expire: res.expire });
    return res;
  }

  // 微信扫码登录
  async loginByWeChatQrCode() {
    const res = await loginByWeChatQrCode({ key: this.loginState.weChatKey });
    runInAction(() => {
      this.userState.limits.creatorLimit = res.creator_trail_limit;
    });
    if (res.errCode == 0) this.afterLogin({ token: res.token, expire: res.expire });
    return res;
  }

  // 获取微信二维码扫码结果
  async getWeChatQrCodeResult(timeout: number) {
    const loopRequest = loopRequestResult(
      getWeChatQrCodeScanStatus,
      { key: this.loginState.weChatKey },
      timeout,
      2000,
      (res: any) => res.errCode === 0 && res.data.scanned,
    );
    return loopRequest;
  }

  // 微信扫码后绑定手机号后登录
  async loginByWeChatWithPhone(code: string) {
    const res = await bindPhoneWithWeChat({
      key: this.loginState.weChatKey,
      code,
      mobileNumber: this.loginState.phone,
      channel_code: sessionStorage.getItem("utm") ?? "",
    });
    if (res.errCode == 0) this.afterLogin({ token: res.token, expire: res.expire });
    return res;
  }

  reset() {
    this.userState = getDefaultUserState();
  }

  // 退出登录
  async logout() {
    if (!isAuthUser()) return;
    try {
      await cancelAllTask({ cancelThirdPartyTasks: true });
      const res = await logout();
      localStorage.removeItem("token");
      this.reset();
      // events.emit(LoginEvent.LOGOUT, {
      //   type: LoginEvent.LOGOUT,
      // });
      if (["/", "login"].includes(location.pathname)) {
        location.replace("/");
      }
      return res;
    } catch (error) {
      console.error("logout error", error);
    }
  }

  setHeaderSearchNode(node: JSX.Element | null) {
    this.headerSearchNode = node;
  }
}

export const loginStore = new LoginStore();
export const useLoginStatus = () => {
  return {
    isGuest: loginStore.isGuest,
    userState: loginStore.userState,
    memberStatus: loginStore.memberStatus,
    loginState: loginStore.loginState,
  };
};
export const setStorage = (key: string, v: any) => {
  localStorage.setItem(`${loginStore.userInfo.userId}/${key}`, v);
};
export const getStorage = (key: string) => {
  return localStorage.getItem(`${loginStore.userInfo.userId}/${key}`);
};
export default LoginStore;
